import { z } from 'zod';

import type { TFunction } from 'i18next';

import { stripHtml } from '@/helpers';

import { KEY_TYPE_LIQPAY, KEY_TYPE_MONOPAY } from '@/constants';

const serviceSchema = z.object({
  id: z.number(),
  name: z.string(),
  price: z.number(),
});

export function createOnlineServiceFormSchema(
  t: TFunction,
  options: { hasLiqpayKey: boolean },
) {
  const { hasLiqpayKey } = options;

  return z
    .object({
      name: z.string().refine((val) => stripHtml(val).trim().length > 0, {
        message: t('validation_required'),
      }),
      description: z.string().optional(),
      locations: z.array(z.string()),
      services: z.array(serviceSchema),
      [KEY_TYPE_LIQPAY]: z.string().nullable(),
      [KEY_TYPE_MONOPAY]: z.string().nullable(),
      order_position: z.string().min(1, { message: t('validation_required') }),
      active: z.boolean(),
      subscription: z.boolean(),
      start_service_required: z.boolean(),
      start_service_id: z.string().nullable().optional(),
      resubscribe_days: z.string().nullable().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.locations.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('validation_required'),
          path: ['locations'],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('validation_select_locations_first'),
          path: ['services'],
        });
      } else if (data.services.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('validation_required'),
          path: ['services'],
        });
      }
    })
    .superRefine((data, ctx) => {
      if (hasLiqpayKey) {
        if (data[KEY_TYPE_LIQPAY] == null || data[KEY_TYPE_LIQPAY] === '') {
          ctx.addIssue({
            code: 'custom',
            message: t('At least one payment key must be selected'),
            path: [KEY_TYPE_LIQPAY],
          });
        }
      } else {
        if (data[KEY_TYPE_MONOPAY] == null || data[KEY_TYPE_MONOPAY] === '') {
          ctx.addIssue({
            code: 'custom',
            message: t('At least one payment key must be selected'),
            path: [KEY_TYPE_MONOPAY],
          });
        }
      }
    })
    .refine(
      (data) => {
        if (data.start_service_required) {
          return data.start_service_id != null && data.start_service_id !== '';
        }
        return true;
      },
      {
        message: t('validation_required'),
        path: ['start_service_id'],
      },
    )
    .refine(
      (data) => {
        if (data.start_service_required) {
          return data.resubscribe_days != null && data.resubscribe_days !== '';
        }
        return true;
      },
      {
        message: t('validation_required'),
        path: ['resubscribe_days'],
      },
    );
}

type OnlineServiceFormSchema = ReturnType<typeof createOnlineServiceFormSchema>;

export type OnlineServiceFormValues = z.infer<OnlineServiceFormSchema>;
